import React from "react";
import { Provider, useAtom } from "jotai";
import { LESSONS } from "@/data/lessons";
import { progressAtom, screenAtom, buildLessonCompletion, type UserProgress } from "@/lib/storage";
import LoginScreen from "@/components/LoginScreen";
import LessonSelect from "@/components/LessonSelect";
import TypingSession from "@/components/TypingSession";
import CodeReview from "@/components/CodeReview";
import "./index.css";

function AppInner() {
  const [progress, setProgress] = useAtom(progressAtom);
  const [screen, setScreen] = useAtom(screenAtom);

  // If progress exists but screen is still login, go to lessons
  const effectiveScreen =
    screen.type === "login" && progress ? { type: "lessons" as const } : screen;

  function handleLogin(name: string) {
    setProgress({ name, completedLessons: [], lessonStats: {} });
    setScreen({ type: "lessons" });
  }

  function handleLogout() {
    setProgress(null);
    setScreen({ type: "login" });
  }

  function handleSelectLesson(lessonId: number) {
    setScreen({ type: "typing", lessonId });
  }

  function handleLessonComplete(lessonId: number, wpm: number, accuracy: number) {
    if (progress) {
      setProgress(buildLessonCompletion(progress, lessonId, wpm, accuracy));
    }
    setScreen({ type: "review", lessonId, wpm, accuracy });
  }

  function handleReviewContinue() {
    setScreen({ type: "lessons" });
  }

  function handleBackToLessons() {
    setScreen({ type: "lessons" });
  }

  switch (effectiveScreen.type) {
    case "login":
      return <LoginScreen onLogin={handleLogin} />;

    case "lessons":
      if (!progress) return <LoginScreen onLogin={handleLogin} />;
      return (
        <LessonSelect
          progress={progress}
          onSelectLesson={handleSelectLesson}
          onLogout={handleLogout}
        />
      );

    case "typing": {
      const lesson = LESSONS.find((l) => l.id === effectiveScreen.lessonId);
      if (!lesson) return null;
      return (
        <TypingSession
          key={effectiveScreen.lessonId}
          lesson={lesson}
          onComplete={(wpm, accuracy) => handleLessonComplete(effectiveScreen.lessonId, wpm, accuracy)}
          onBack={handleBackToLessons}
          onSwitchLesson={handleSelectLesson}
        />
      );
    }

    case "review": {
      const lesson = LESSONS.find((l) => l.id === effectiveScreen.lessonId);
      if (!lesson) return null;
      return (
        <CodeReview
          lesson={lesson}
          wpm={effectiveScreen.wpm}
          accuracy={effectiveScreen.accuracy}
          onContinue={handleReviewContinue}
        />
      );
    }
  }
}

export function App() {
  return (
    <Provider>
      <AppInner />
    </Provider>
  );
}

export default App;
